import { ReactNode, Ref } from "react"

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  TableSortLabel,
  Tooltip,
} from "@mui/material"
import { DashboardSortDirection } from "ts/types/AdminDashboard"

export interface IDashboardTableColumn<Row, SortKey extends string> {
  id: string
  label: string
  sortKey?: SortKey
  align?: "left" | "right"
  // Grays the header out, drops its sort and explains why in a tooltip.
  disabledReason?: string
  render: (row: Row) => ReactNode
}

const focusRingSx = {
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "mockup.primary60",
    outlineOffset: "2px",
  },
}

// The first column names the row: a row header pinned to the left while the
// other columns scroll under it.
const stickySx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  borderRight: "1px solid",
  borderRightColor: "mockup.neutral90",
}

const DashboardTable = <Row, SortKey extends string>({
  label,
  columns,
  rows,
  getRowId,
  sortBy,
  sortDir,
  onSort,
  skeletonRows,
  regionRef,
}: {
  label: string
  columns: IDashboardTableColumn<Row, SortKey>[]
  rows: Row[]
  getRowId: (row: Row) => string | number
  sortBy: SortKey
  sortDir: DashboardSortDirection
  onSort: (key: SortKey) => void
  // First load: the real headers over this many placeholder rows.
  skeletonRows?: number
  regionRef?: Ref<HTMLDivElement>
}) => (
  <Box
    ref={regionRef}
    role="region"
    aria-label={label}
    aria-busy={skeletonRows ? true : undefined}
    tabIndex={0}
    sx={{
      overflowX: "auto",
      // Visually hidden cell text is absolutely positioned; anchored here it's
      // clipped with the table instead of widening the page.
      position: "relative",
      borderRadius: "8px",
      ...focusRingSx,
    }}
  >
    <Table
      size="small"
      sx={{
        minWidth: 880,
        "& .MuiTableCell-root": {
          px: 0,
          py: 1.25,
          borderBottom: "none",
          fontSize: "14px",
        },
        "& .MuiTableCell-head": {
          bgcolor: "mockup.primary95",
          color: "mockup.primary60",
          px: 1,
          fontSize: "12px",
          fontWeight: 700,
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        },
        "& .MuiTableCell-head.is-disabled": {
          bgcolor: "mockup.neutral95",
          color: "mockup.neutral30",
        },
        "& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-root:hover":
          { color: "inherit" },
        "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
          color: "inherit",
        },
        // MUI reverses the label in right-aligned cells; every arrow sits after it.
        "& .MuiTableCell-alignRight .MuiTableSortLabel-root": {
          flexDirection: "row",
        },
        "& .MuiTableBody-root th": { px: 1 },
        "& .MuiTableBody-root .MuiTableCell-root": { py: 0 },
        "& .MuiTableBody-root .MuiTableRow-root": {
          height: 44,
          bgcolor: "mockup.neutral100",
        },
        "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
          bgcolor: "mockup.primary99",
        },
      }}
    >
      <TableHead>
        <TableRow>
          {columns.map((column, index) => {
            const isActive = !column.disabledReason && column.sortKey === sortBy
            return (
              <TableCell
                key={column.id}
                align={column.align}
                className={column.disabledReason ? "is-disabled" : undefined}
                sortDirection={isActive ? sortDir : false}
                sx={index === 0 ? { ...stickySx, zIndex: 2 } : undefined}
              >
                {column.disabledReason ? (
                  <Tooltip title={column.disabledReason} describeChild>
                    <Box component="span" tabIndex={0} sx={focusRingSx}>
                      {column.label}
                    </Box>
                  </Tooltip>
                ) : column.sortKey ? (
                  <TableSortLabel
                    active={isActive}
                    direction={isActive ? sortDir : "desc"}
                    onClick={() => column.sortKey && onSort(column.sortKey)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            )
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: skeletonRows ?? 0 }, (_, index) => (
          <TableRow key={index} data-testid="table-skeleton-row">
            <TableCell colSpan={columns.length} sx={{ px: 1 }}>
              <Skeleton variant="rounded" height={36} />
            </TableCell>
          </TableRow>
        ))}
        {rows.map((row) => (
          <TableRow key={getRowId(row)}>
            {columns.map((column, index) =>
              index === 0 ? (
                <TableCell
                  key={column.id}
                  component="th"
                  scope="row"
                  sx={{
                    ...stickySx,
                    bgcolor: "inherit",
                    maxWidth: 160,
                    overflowWrap: "anywhere",
                    fontWeight: 600,
                    color: "mockup.neutral10",
                  }}
                >
                  {column.render(row)}
                </TableCell>
              ) : (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    color: "mockup.neutral10",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* The theme draws the column divider on this class */}
                  <Box className="mui-body-cell-border" sx={{ px: 1 }}>
                    {column.render(row)}
                  </Box>
                </TableCell>
              ),
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
)

export default DashboardTable
